import { ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, TemplateRef, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentStateService, DirtyFlagService } from '@ddv/behaviors';
import { hasOwnProperty } from '@ddv/charts';
import { ConfirmationPopupService, ModalDialogRef, MultiSubscriptionComponent } from '@ddv/common-components';
import {
    appWidgetStateToConfig,
    canEnterEditMode,
    DashBoardComponent,
    DashboardDetails,
    DashboardNote,
    DashboardNotesService,
    DashboardService,
    DashboardSharingService,
    getUpdateDashboardRequest,
    SaveDashboardFormComponent,
    SaveDashboardNote,
    ViewTile,
} from '@ddv/dashboards';
import { DatasetManagerService } from '@ddv/datasets';
import { UserData, UserEntitlements, UserService } from '@ddv/entitlements';
import { QueryParamsService } from '@ddv/filters';
import {
    AppLayoutState,
    createWorkspaceObj,
    LayoutService,
    ManagerService,
    setWidgetExtraParamsFromDashboardDetails,
    TopLevelWorkspaceComponent,
    Workspace,
} from '@ddv/layout';
import {
    AppWidgetState,
    Client,
    DashboardModel,
    DashboardPreference,
    FuzzyDates,
    getGlobalSaveDialogOptions,
    GLOBAL_SAVE_MESSAGE,
    GLOBAL_SAVE_NOTE,
    MANAGE_WIDGET_WS_ID,
    MODE,
    NamedQuery,
    QueryPeriodTypeName,
    UserPreferences,
    WIDGET_LIFECYCLE_EVENT,
} from '@ddv/models';
import { InitializationError, InitializationErrorCollectorService } from '@ddv/navigation';
import { FuzzyDateInit, FuzzyDatesService } from '@ddv/reference-data';
import { deepClone } from '@ddv/utils';
import { DdvWidgetService } from '@ddv/widget-on-boards';
import { FuzzyDate } from '@hs/ui-core-date';
import { Fund } from '@hs/ui-core-fund';
import { combineLatest, Observable, Subject, Subscription, of, map, firstValueFrom } from 'rxjs';

import { DashboardNavService } from '../../services/dashboard-nav.service';
import { RevertDashboardService } from '../../services/revert-dashboard.service';
import { DashboardNavComponent } from '../dashboard-nav/dashboard-nav.component';
import { HsDashboardComponent } from '../hs-dashboard/hs-dashboard.component';

@Component({
    templateUrl: 'view-edit-dashboard.component.html',
    styleUrls: ['view-edit-dashboard.component.scss'],
    standalone: false,
})
export class ViewEditDashboardComponent extends MultiSubscriptionComponent implements OnInit, OnDestroy, TopLevelWorkspaceComponent {
    @Input() entitlements: UserEntitlements | undefined;
    @Input() user: UserData | undefined;
    @Input() preferences: UserPreferences | undefined;
    @Input() clients: Client[] | undefined;
    @Input() fuzzyDates: FuzzyDate[] | undefined;
    @Input() funds: Fund[] = [];

    @ViewChild('navComponent', { static: true }) dashboardNavComponent: DashboardNavComponent | undefined;
    @ViewChild('gridcanvas', { static: true }) gridcanvas: ElementRef | undefined;
    @ViewChild('dashboardContainer', { static: true }) dashboardContainer: ElementRef | undefined;
    // i do not know why this is called "dummy" as it does play a role in the layout sizing logic
    @ViewChild('dummyDashboardDiv', { static: true }) dummyDashboardDiv: ElementRef | undefined;
    @ViewChild('saveGlobalTemplate') saveGlobalTemplate: TemplateRef<string> | undefined;
    @ViewChild('hsDashboard', { static: true }) private readonly hsDashboardRef: HsDashboardComponent | undefined;
    @ViewChild('dashboardComponent', { static: true }) private readonly dashboardComponent: DashBoardComponent | undefined;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly dashboardService = inject(DashboardService);
    private readonly dashboardNavService = inject(DashboardNavService);
    private readonly dashboardNotesService = inject(DashboardNotesService);
    private readonly datasetManagerService = inject(DatasetManagerService);
    private readonly ddvWidgetService = inject(DdvWidgetService);
    private readonly dirtyFlagService = inject(DirtyFlagService);
    private readonly fuzzyDatesService = inject(FuzzyDatesService);
    private readonly initializationErrorsService = inject(InitializationErrorCollectorService);
    private readonly layoutService = inject(LayoutService);
    private readonly manager = inject(ManagerService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly revertDashboardService = inject(RevertDashboardService);
    private readonly router = inject(Router);
    private readonly sharingService = inject(DashboardSharingService);
    private readonly userService = inject(UserService);

    inPresentationMode = true;
    isDashboardLoading = true;
    isGlobal = false;
    saveGlobalMessage = GLOBAL_SAVE_MESSAGE;
    saveGlobalNote = GLOBAL_SAVE_NOTE;
    currentDashboardId: string | undefined;
    workspace: Workspace | undefined;

    protected widgets: AppWidgetState[] | undefined;
    protected clientCode: string | undefined;
    protected dashboardNote: DashboardNote | undefined;
    protected isGlobalEditPartial = false;
    protected useNewExports = false;
    protected isMultiClient = false;
    protected ddvFuzzyDates: FuzzyDates = new FuzzyDates();
    protected dashboard: DashboardDetails | undefined;
    protected dashboardPrefs: DashboardPreference | undefined;
    protected initializationErrors: InitializationError[] = [];
    protected memberOf: ViewTile | undefined;

    private leftEditDirty = false;
    private boardModeAndTilesSubscription: Subscription | undefined;
    private dashboardPrefsSnapshot: DashboardPreference | undefined;
    private adhocFuzzyDates: FuzzyDate[] | undefined;
    private investorFuzzyDates: FuzzyDate[] | undefined;
    private boardQueryParamsSubscription: Subscription | undefined;

    override ngOnDestroy(): void {
        // this could be handled better in the route guard as you could check if you leaving to head to MW and purge
        // all workspaces on that condition
        this.handleDetailWidgetsOnBoard();
        this.manager.removeWorkspace();

        this.boardModeAndTilesSubscription?.unsubscribe();
        this.boardQueryParamsSubscription?.unsubscribe();

        super.ngOnDestroy();
    }

    ngOnInit(): void {
        this.initializationErrorsService.errors$
            .subscribe({
                next: (errors) => this.initializationErrors = errors,
            });

        this.layoutService.setTopLevelWorkspaceComponent(this);
        this.layoutService.initializeLayoutManager();
        this.manager.registerWorkspaceStateChangeHandler(this.onWorkspaceChangeEvent.bind(this));

        this.boardQueryParamsSubscription = this.queryParamsService.dashboardQueryParams
            .subscribe({
                next: (dashboardPref) => {
                    if (!this.dashboardPrefsSnapshot && hasOwnProperty(dashboardPref, 'startDate')) {
                        this.dashboardPrefsSnapshot = dashboardPref;
                    }
                    this.dashboardPrefs = dashboardPref;
                },
            });

        this.subscribeTo(this.currentStateService.clientCode$, (clientCode: string) => {
            this.clientCode = clientCode;
        });

        if (this.entitlements?.hasNoPermissions()) {
            throw new Error('You are not entitled to anything');
        }

        this.isGlobalEditPartial = !!this.entitlements?.hasGlobalEditPartial();
        this.initializeWorkspace();

        this.activatedRoute.queryParams
            .subscribe({
                next: (params) => this.useNewExports = (params.useNewExports === 'true'),
            });

        this.currentStateService.isMultiClient$
            .subscribe({
                next: (isMultiClient) => this.isMultiClient = isMultiClient,
            });
        this.fuzzyDatesService.fuzzyDates()
            .subscribe({
                next: (fuzzyDates) => this.ddvFuzzyDates = fuzzyDates,
            });
    }

    guardAgainstUnsavedChanges(): boolean | Observable<boolean> {
        if (this.inPresentationMode || !this.dirtyFlagService.isDirty) {
            return true;
        }

        const canContinue = new Subject<boolean>();
        const confirmDialogOptions = {
            message: 'You have un-saved changes. What would you like to do:',
            confirmButtonText: 'Continue Editing',
            denyButtonText: 'Discard Changes',
        };

        this.confirmationService.showConfirmationPopup(confirmDialogOptions, true).subscribe({
            next: (action) => {
                if (action === 'confirm') {
                    this.continueEditingCallback(canContinue);
                } else if (action === 'deny') {
                    this.discardChangesCallback(canContinue);
                }
            },
        });

        return canContinue;
    }

    async onDashboardSelectedInLeftNav(dashboardId: string): Promise<void> {
        await this.navigateToBoardAndMode(dashboardId, this.presentationModeAsString);
    }

    async onCancelEdit(): Promise<void> {
        await this.navigateToBoardAndMode(this.currentDashboardId, MODE.VIEW);
    }

    onRevertChanges(): void {
        this.manager.removeWorkspace();
        this.manager.selectWidget(false, undefined);
        this.activateDashboardById(this.currentDashboardId!);
        this.dirtyFlagService.exitDirtyState();
    }

    onSaveWidgetChangesToServer(queryParams: DashboardPreference): void {
        const dashboardExtraParams = this.manager.getWorkspace()?.getExtraParameters();

        if (dashboardExtraParams?.isGlobal && !this.isGlobalEditPartial) {
            const confirmDialogOptions = getGlobalSaveDialogOptions(this.saveGlobalTemplate);
            this.confirmationService.showConfirmationPopup(confirmDialogOptions).subscribe({
                next: (action) => {
                    if (action === 'confirm') {
                        this.saveWorkspaceConfigurationToCurrentDashboard(queryParams);
                    }
                },
            });
        } else {
            this.saveWorkspaceConfigurationToCurrentDashboard(queryParams);
        }
    }

    saveWorkspaceConfigurationToCurrentDashboard(queryParams: DashboardPreference): void {
        this.isDashboardLoading = true;

        const namedQueries = this.manager.getExtraParametersForWorkspace()?.widgets.reduce((queries: NamedQuery[], widget) => {
            if (widget.namedQuery) {
                queries.push(widget.namedQuery);
            }
            return queries;
        }, []) ?? [];
        const isCalendarVisible = !namedQueries.length || namedQueries.some((nq) => nq.periodType !== QueryPeriodTypeName.NO_CALENDAR);

        if (!isCalendarVisible) {
            queryParams.startDate = '';
            queryParams.endDate = '';
        }

        queryParams.filters?.forEach((filter) => {
            filter.filterValuesType = this.getFilterValuesType(filter.values ?? []);
            filter.values = filter.values?.map((value, index) => {
                if (!!filter.fuzzies?.[index] && filter.fuzzies[index] !== '') {
                    return filter.fuzzies[index];
                }
                return value;
            });
        });

        const savePayload = getUpdateDashboardRequest(queryParams, this.manager.getAppWidgetsState(), this.dashboard?.version ?? 0);
        this.dashboardService.updateDashboard(this.clientCode ?? '', this.dashboard?.id ?? 0, savePayload)
            .subscribe({
                next:
                    async (dashboard: DashboardDetails | DashboardModel) => {
                        this.dirtyFlagService.exitDirtyState();

                        dashboard.dashboardPreference?.filters?.forEach((filter) => {
                            if (filter.filterValuesType === 'number') {
                                filter.values = filter.values?.map((value) => Number(value));
                            }

                            if (filter.filterValuesType === 'boolean') {
                                filter.values = filter.values?.map((value) => {
                                    if (value != null) {
                                        return value === 'true';
                                    } else {
                                        return value;
                                    }
                                });
                            }
                        });

                        const savedParams = dashboard.dashboardPreference;

                        const updatedDashboard = this.manager.getWorkspace();

                        dashboard.widgets = updatedDashboard?.extraParameters?.widgets ?? [];

                        if (this.dashboard) {
                            this.dashboard.version = dashboard.version;
                        }

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        this.manager.updateWidgetsVersion(dashboard.widgets as any);

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setWidgetExtraParamsFromDashboardDetails(dashboard as any, updatedDashboard!);
                        this.updateDashboardQueryParams(savedParams);
                        this.updateWidgetExtraParams(updatedDashboard?.getExtraParameters()?.widgets ?? []);
                        await this.navigateToBoardAndMode(this.currentDashboardId, 'view');
                    },
                error: (error) => this.onDashboardSaveError(error),
            });
    }

    onWorkspaceChangeEvent(appLayoutState: AppLayoutState, isWorkspaceRendered: boolean, eventName: WIDGET_LIFECYCLE_EVENT): void {
        if (eventName === WIDGET_LIFECYCLE_EVENT.INIT_WIDGET && isWorkspaceRendered) {
            // Should only execute once, after widgets are rendered
            this.layoutService.updateWidgetBehaviour(false);
            this.hsDashboardRef?.setHighlightVisibility(!this.inPresentationMode);
        }

        if (!this.inPresentationMode && isDirtyOnWorkspaceChange(isWorkspaceRendered, eventName)) {
            this.dirtyFlagService.enterDirtyState(this.manager.getCurrentDashboardId() ?? '');
        }

        this.cdr.detectChanges();
    }

    protected async onEditRequested(dashboardId: string): Promise<boolean> {
        return this.router.navigate([`../../edit/${dashboardId}`], { relativeTo: this.activatedRoute });
    }

    protected onDeleteDashboardNote(): void {
        if (!this.currentDashboardId) {
            throw new Error('No dashboard id found');
        }

        this.dashboardNotesService.deleteDashboardNote(this.currentDashboardId).subscribe({
            next: () => this.dashboardNote = undefined,
        });
    }

    protected onSaveDashboardNote(saveDashboardNote: SaveDashboardNote): void {
        if (!this.currentDashboardId) {
            throw new Error('No dashboard id found');
        }

        let newDashboardNote$: Observable<DashboardNote | undefined>;
        const dashboard = this.dashboardService.getCurrentDashboard();

        if (saveDashboardNote.status === 'new') {
            newDashboardNote$ = this.dashboardNotesService.createDashboardNote(
                this.currentDashboardId,
                saveDashboardNote.dashboardNote,
                dashboard?.clientCode,
            );
        } else {
            newDashboardNote$ = this.dashboardNotesService.updateDashboardNote(this.currentDashboardId, saveDashboardNote.dashboardNote);
        }

        newDashboardNote$.subscribe({
            next: (dashboardNote) => this.dashboardNote = dashboardNote,
        });
    }

    protected saveDashboardFilters(filtersToRemove: string[]): void {
        if (this.inPresentationMode || !this.dashboardPrefsSnapshot?.filters || !this.dashboardPrefs?.filters) {
            return;
        }

        const dashboardFilters = this.dashboardPrefsSnapshot.filters.map((f) => f.name ?? '');
        this.dashboardPrefsSnapshot.filters = this.dashboardPrefsSnapshot.filters.filter((f) => !filtersToRemove.includes(f.name ?? ''));
        this.dashboardPrefs.filters = this.dashboardPrefs.filters.filter((f) => !filtersToRemove.includes(f.name ?? ''));

        if (dashboardFilters.every((df) => this.dashboardPrefs?.filters?.map((f) => f.name ?? '')?.includes(df))) {
            this.queryParamsService.dispatchUpdatedQueryParams(this.dashboardPrefs);
            return;
        }

        const newFilters = this.dashboardPrefsSnapshot.filters;
        const currentDashboardPrefs = structuredClone(this.dashboardPrefs);
        this.dashboardPrefsSnapshot = undefined;
        this.dashboardService.updateDashboardFilters(this.clientCode ?? '', Number(this.currentDashboardId), newFilters)
            .subscribe({
                next: (dashboardModel: DashboardDetails) => {
                    dashboardModel.dashboardPreference = currentDashboardPrefs;
                    this.manager.setExtraParametersForWorkspace(new DashboardModel(dashboardModel));
                    this.queryParamsService.dispatchUpdatedQueryParams(dashboardModel.dashboardPreference ?? {});
                },
            });
    }

    protected savePreferences(updatedPreferences: UserPreferences): void {
        this.userService.updateUserPreferenceData(this.clientCode, updatedPreferences)
            .subscribe({
                next: () => this.preferences = updatedPreferences,
            });
    }

    private initializeWorkspace(): void {
        if (!this.manager.getCurrentDashboardId() || this.manager.getCurrentDashboardId() === MANAGE_WIDGET_WS_ID) {
            this.manager.removeAllWidgets();
            this.manager.removeWorkspace();
            this.layoutService.setTopLevelWorkspaceComponent(this);
            this.layoutService.initializeLayoutManager();
            this.manager.registerWorkspaceStateChangeHandler(this.onWorkspaceChangeEvent.bind(this));
        }

        if (this.hsDashboardRef) {
            this.hsDashboardRef.isLastDashboardClosed = true;
        }

        this.boardModeAndTilesSubscription = combineLatest([
            this.currentStateService.dashboardModeAndId$,
            this.dashboardNavService.viewTiles$,
        ]).subscribe({
            next: async ([{ id, mode }, tiles]): Promise<void> => {
                // In practice, this will only ever happen in tests
                if (!id && !mode) {
                    return;
                }

                if (!id) {
                    await (tiles.length ? this.navigateToBoardAndMode(tiles[0].links[0].id, 'view') : this.goToNoDashboard());
                    return;
                }

                if (id.toString() === this.currentDashboardId) {
                    const viewLink = tiles.flatMap((t) => t.links).find((l) => l.id === id);
                    const dashboard = this.dashboardService.getCurrentDashboard();
                    if (viewLink && dashboard && mode === 'edit' && await this.notAllowedToEnterEditMode(id, dashboard)) {
                        await this.navigateToBoardAndMode(id, 'view');
                        return;
                    }
                }
                this.respondToRouteChange(id, mode ?? undefined, tiles);
            },
        });
    }

    private async notAllowedToEnterEditMode(id: number, dashboard: DashboardDetails): Promise<boolean> {
        return dashboard.privilege === 'VIEW' || await firstValueFrom(this.hasNoPermissionToEnterEditMode(id, dashboard));
    }

    private respondToRouteChange(dashboardId: number, viewMode: string | undefined, tiles: ViewTile[]): void {
        const newDashboardId = dashboardId?.toString();
        const selectedDashboardHasChanged = newDashboardId !== this.currentDashboardId;

        this.currentDashboardId = newDashboardId;
        this.memberOf = tiles.find((t) => t.isGroup && t.links.some((link) => link.id.toString() === this.currentDashboardId));
        if (!tiles.length) {
            this.workspace = undefined;
            this.dashboard = undefined;
        }

        const newModeIsPresentation = viewMode === MODE.VIEW;
        const modeHasChanged = newModeIsPresentation !== this.inPresentationMode;
        const transitionIsFromEditToView = modeHasChanged && !this.inPresentationMode && viewMode;
        this.inPresentationMode = newModeIsPresentation;

        this.configureViewForMode();

        if (selectedDashboardHasChanged) {
            this.handleDetailWidgetsOnBoard();
            this.manager.removeWorkspace();
            this.activateDashboardById(this.currentDashboardId);
            this.fetchDashboardNote(this.currentDashboardId);
        } else if (modeHasChanged) {
            this.layoutService.updateWidgetBehaviour(false);

            if (transitionIsFromEditToView && this.leftEditDirty) {
                // this is a bit of a hack, but the dashboard/workspace/widget/event flow
                // is impossible to track.  this causes the underlying workspace to not respond
                if (this.dashboardComponent) {
                    this.dashboardComponent.resetCurrentWorkspace();
                }

                this.manager.removeAllWidgets();
                this.activateDashboardById(this.currentDashboardId);
            } else {
                this.isDashboardLoading = false;
            }
        }

        this.layoutService.cancelWorkspaceChanges();
        this.leftEditDirty = false;
        const dashboard = this.dashboardService.getCurrentDashboard();
        if (dashboard?.id?.toString() === this.currentDashboardId) {
            this.isGlobal = dashboard.isGlobal;
        }
    }

    private async goToNoDashboard(): Promise<void> {
        this.inPresentationMode = true;
        await this.navigateToBoardAndMode(undefined, 'view');
        this.isDashboardLoading = false;
    }

    private configureViewForMode(): void {
        this.layoutService.changeWorkspaceMode(this.inPresentationMode ? MODE.VIEW : MODE.EDIT_WORKSPACE);
        this.hsDashboardRef?.setHighlightVisibility(!this.inPresentationMode);
    }

    private discardChangesCallback(canContinue: Subject<boolean>): void {
        this.leftEditDirty = true;
        this.manager.selectWidget(false, undefined);
        this.dirtyFlagService.exitDirtyState();
        canContinue.next(true);
    }

    private continueEditingCallback(canContinue: Subject<boolean>): void {
        canContinue.next(false);
    }

    private async navigateToBoardAndMode(dashboardId: string | number | undefined, mode: string): Promise<boolean> {
        return this.router.navigate([`../../${mode}/${dashboardId ?? 'none'}`], { relativeTo: this.activatedRoute });
    }

    private activateDashboardById(dashboardId: string): void {
        this.isDashboardLoading = true;
        this.dashboardService.fetchDashboard(this.clientCode ?? '', Number(dashboardId))
            .subscribe({
                next: (dashboard) => {
                    this.dashboard = dashboard;

                    if (dashboard.isSingleNamedQuery() && dashboard.isAdhoc()) {
                        this.fuzzyDatesService.getFuzzyDatesForAdhoc(dashboard.widgets[0].namedQuery!)
                            .subscribe({
                                next: (adhocFuzzyDates) => this.addAdhocFuzzyDates(adhocFuzzyDates),
                            });
                    } else {
                        this.removeAdhocFuzzyDates();
                    }

                    if (dashboard.isInvestorOnly()) {
                        const fundCodes = dashboard.getAllFundCodes();
                        const namedQueryIds = dashboard.getNamedQueryIds();
                        this.fuzzyDatesService.getInvestorFuzzyDates(fundCodes, namedQueryIds)
                            .subscribe({
                                next: (investorFuzzyDates) => this.addInvestorFuzzyDates(investorFuzzyDates),
                            });
                    } else {
                        this.removeInvestorFuzzyDates();
                    }

                    this.addInfinityWorkspace(createWorkspaceObj(dashboard));
                },
                error: (error) => {
                    console.error(error);
                    this.dashboardNavService.removeViewTile(Number(dashboardId));
                    this.navigateToBoardAndMode(undefined, this.presentationModeAsString).catch((err) => console.error(err));
                },
            });
    }

    private addAdhocFuzzyDates(adhocFuzzyDates: FuzzyDateInit[]): void {
        this.adhocFuzzyDates = adhocFuzzyDates.map((init) => new FuzzyDate(init.name, init.currentValue));
        this.fuzzyDates = [
            ...(this.fuzzyDates ?? []),
            ...this.adhocFuzzyDates,
        ];
    }

    private removeAdhocFuzzyDates(): void {
        this.fuzzyDates = this.fuzzyDates?.filter((ccFuzzyDate) => !this.adhocFuzzyDates?.includes(ccFuzzyDate));
        this.adhocFuzzyDates = undefined;
    }

    private addInvestorFuzzyDates(investorFuzzyDates: FuzzyDateInit[]): void {
        this.investorFuzzyDates = investorFuzzyDates.map((init) => new FuzzyDate(init.name, init.currentValue));
        this.fuzzyDates = [
            ...(this.fuzzyDates ?? []),
            ...this.investorFuzzyDates,
        ];
    }

    private removeInvestorFuzzyDates(): void {
        this.fuzzyDates = this.fuzzyDates?.filter((ccFuzzyDate) => !this.investorFuzzyDates?.includes(ccFuzzyDate));
        this.investorFuzzyDates = undefined;
    }

    private fetchDashboardNote(dashboardId: string): void {
        // Rework this after dashboards are migrated and notes come along with them
        this.subscribeTo(this.dashboardNotesService.fetchDashboardNote(dashboardId), (dashboardNote: DashboardNote | undefined) => {
            this.dashboardNote = dashboardNote;
        });
    }

    private addInfinityWorkspace(workspace: Workspace): void {
        this.manager.addWorkspace(workspace);
        this.workspace = workspace;
        this.initiateDashboardSwitch();

        const currDashboardModel = workspace.getExtraParameters();
        this.dashboardService.openDashboard(this.clientCode ?? '', Number(workspace.id))
            .subscribe({
                next: () => {
                    this.dashboardNavService.fetchOpenViewTiles(this.clientCode ?? '').subscribe();
                    this.manager.createWidgets(appWidgetStateToConfig(currDashboardModel?.widgets, this.layoutService.getWorkspaceMode()));
                    this.widgets = this.manager.getAppWidgetsState();
                },
            });
    }

    private initiateDashboardSwitch(): void {
        this.datasetManagerService.clearDashboardPreference();
        if (this.hsDashboardRef) {
            this.hsDashboardRef.isLastDashboardClosed = false;  // this looks suspect
        }
        this.layoutService.resetLayoutForNewDashboard();
        this.isDashboardLoading = false;
    }

    private get presentationModeAsString(): string {
        return this.inPresentationMode ? MODE.VIEW : 'edit';
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private onDashboardSaveError(error: any, form?: ModalDialogRef<void, SaveDashboardFormComponent>): void {
        if (!error._body) {
            return;
        }

        const errorDetails = typeof error._body === 'string' ? JSON.parse(error._body) : error._body;

        if (errorDetails.message === 'Dashboard name is already in use') {
            return form?.componentInstance.nameUnavailable();
        }

        if (errorDetails.message === 'Dashboard version is stale') {
            if (form) {
                form.close();
            }

            this.handleStaleDashboardVersion(errorDetails.message);
        }
    }

    private updateWidgetExtraParams(widgets: AppWidgetState[]): void {
        widgets.forEach((widget) => this.manager.setWidgetExtraPreferences(widget.id, widget));
    }

    private updateDashboardQueryParams(dashboardPreference: DashboardPreference): void {
        const dashboard = this.manager.getWorkspace();
        const extraParams = dashboard?.getExtraParameters();
        const viewPreference = deepClone(dashboardPreference);
        const editPreference = deepClone(dashboardPreference);
        viewPreference.dashboardPreferenceMode = MODE.VIEW;
        editPreference.dashboardPreferenceMode = MODE.EDIT_WORKSPACE;
        if (extraParams) {
            extraParams.dashboardPreferences = [viewPreference, editPreference];
            dashboard?.setExtraParameters(extraParams);
        }
    }

    private hasNoPermissionToEnterEditMode(
        dashboardId: number,
        dashboard: DashboardDetails,
    ): Observable<boolean> {
        if (!this.entitlements) {
            return of(true);
        }

        const userEntitlements = this.entitlements;
        return this.sharingService.getSharing(this.clientCode!, dashboardId)
            .pipe(
                map((sharing) => {
                    const dashboardDetails = dashboard;
                    return !canEnterEditMode(dashboardDetails.createdBy, dashboardDetails.isGlobal, sharing, userEntitlements);
                }),
            );
    }

    private handleDetailWidgetsOnBoard(): void {
        if (this.workspace?.extraParameters?.widgets.some((widget) => widget.isDetailWidget)) {
            this.ddvWidgetService.resetDetailWidgetAndViewQueryParams();
        }
    }

    private getFilterValuesType(filterValues: (string | number | boolean)[]): string {
        const allValuesTypes: string[] = filterValues?.map((value) => {
            if (typeof value === 'object' && value == null) {
                return 'null';
            }

            return typeof value;
        });

        const valuesTypes = Array.from(new Set(allValuesTypes));
        const type = valuesTypes.length === 1 ?
            valuesTypes[0] :
            valuesTypes.length === 2 ? valuesTypes.find((valuesType) => valuesType !== 'null') : '';

        return type ?? '';
    }

    private handleStaleDashboardVersion(message: string): void {
        this.confirmationService.showConfirmationPopup({ message }, true).subscribe({
            next: (action) => {
                if (action === 'confirm') {
                    this.revert();
                }
            },
        });
    }

    private revert(): void {
        const dashboardId = this.manager.getCurrentDashboardId();
        this.manager.removeAllWidgets();
        if (dashboardId) {
            this.revertDashboardService.revert(dashboardId, this.layoutService.getWorkspaceMode(), this.clientCode ?? '');
        }
    }
}

function isDirtyOnWorkspaceChange(isWorkspaceRendered: boolean, eventName: WIDGET_LIFECYCLE_EVENT): boolean {
    const nonDirtyingEvents = [
        WIDGET_LIFECYCLE_EVENT.INIT_WIDGET,
        WIDGET_LIFECYCLE_EVENT.AFTER_CASCADE,
        WIDGET_LIFECYCLE_EVENT.AFTER_MAXIMIZE,
        WIDGET_LIFECYCLE_EVENT.AFTER_WORKSPACE_RESIZE,
        WIDGET_LIFECYCLE_EVENT.ON_CLOSE,
    ];
    return isWorkspaceRendered && !nonDirtyingEvents.includes(eventName);
}
